import React, { useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import UserAvatar from "./UserAvatar";
import ActivationLinkActions from "./ActivationLinkActions";
import ActivationCountdown from "./ActivationCountdown";
import { RoleLimitsTable, __setRoleSummaryCardInfoSource } from "./RoleSummaryCard";
import "./SuccessResultCard.css";

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["successResultCard"]?.[key] || fallback;

// Result shown after addAdminFamily's UpdateUserRoleByAdmin call succeeds --
// works for both cases (mission 1): a brand-new user (userCreated:true) and
// an existing one whose role was granted/updated (userCreated:false, with
// roleCreated or roleUpdated telling those two existing-user sub-cases apart).
// Never closes itself -- stays up with the three ActivationLinkActions
// (Share/Copy/QR, the same shared component CardUser's modal uses) until the
// admin explicitly clicks "Finish" (mission 12/15).
//
// `shareMessage` (optional): addAdminFamily's existing, already-prepared
// invitation paragraph -- passed straight through to ActivationLinkActions,
// which appends the link to it rather than replacing it (mission 3).
const SuccessResultCard = ({ infoSource, operationResult, activationUrl, shareMessage, onFinish, onSuccess, onError }) => {
  __setElakayInfoSource(infoSource);
  __setRoleSummaryCardInfoSource(infoSource);
  // Self-contained toast so copy/share feedback is always visible here, even
  // when the host page has no alert slot of its own for this view (this card
  // fully replaces the form while shown, so addAdminFamily's usual alert
  // area isn't rendered) -- still calls onSuccess/onError too, for callers
  // that do want to layer their own alert on top.
  const [toast, setToast] = useState(null); // { type: "success"|"error", text }
  const [expired, setExpired] = useState(false);
  if (!operationResult) return null;

  const handleSuccess = (text) => {
    setToast({ type: "success", text });
    onSuccess?.(text);
  };
  const handleError = (text) => {
    setToast({ type: "error", text });
    onError?.(text);
  };

  const { userCreated, roleUpdated, user, assignedRole } = operationResult;
  const fullName = `${user?.firstname || ""} ${user?.lastname || ""}`.trim();
  // Single source of truth for "which role was just selected/assigned"
  // (mission 1/2) -- always assignedRole.roleName, never a leftover variable,
  // never hardcoded "Provider".
  const roleName = assignedRole?.roleName || "";
  const expiresAt = operationResult?.activation?.expiresAt || null;

  // mission section 1/2: a distinct message per case -- never the same
  // wording for a new user vs. an existing one.
  const heading = userCreated
    ? __elakayText("headingCreated", "User added successfully")
    : __elakayText("headingRoleAdded", "Role added successfully");

  const bodyMessage = userCreated
    ? __elakayText("bodyCreated", "The user has been created successfully. Share their invitation so they can activate their account and accept the {role} role.").replace("{role}", roleName)
    : roleUpdated
      ? __elakayText("bodyRoleUpdated", "This user's role has been updated successfully. A new link is available to confirm the changes.")
      : __elakayText("bodyRoleGranted", "This user already has an account. The {role} role has been assigned to them. Share the link so they can review and accept this change.").replace("{role}", roleName);

  // Matches the mission's two mockups exactly: a brand-new user always shows
  // "Invitation pending" (+ expiry); any existing user (role granted or
  // updated) shows "Existing account" instead, regardless of whether this
  // particular invite token is itself new.
  const invitationStatusLabel = userCreated
    ? __elakayText("invitationPending", "Invitation pending")
    : __elakayText("existingAccount", "Existing account");

  return (
    <div className="success-result-card">
      <div className="success-result-card-header">
        <FaCheckCircle className="success-result-card-icon" />
        <h4>{heading}</h4>
      </div>

      <div className="success-result-card-identity">
        <UserAvatar userId={user?._id} fullName={fullName} className="success-result-card-avatar" />
        <div>
          <p className="success-result-card-name">{fullName || __elakayText("newUser", "New user")}</p>
          {user?.email && <p className="success-result-card-email">{user.email}</p>}
        </div>
      </div>

      <p className="success-result-card-message">{bodyMessage}</p>

      <div className="success-result-card-meta">
        {roleName && (
          <div>
            <span className="success-result-card-meta-label">{__elakayText("roleAssigned", "Role assigned")}</span>
            <span className="success-result-card-role-badge">{roleName}</span>
          </div>
        )}
        <div>
          <span className="success-result-card-meta-label">{invitationStatusLabel}</span>
          {expiresAt && (
            <span className="success-result-card-expiry">
              {__elakayText("expiresOn", "Expires on")} {new Date(expiresAt).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      {/* mission 1.2: limits/data of the role actually assigned -- never a
          fixed role's limits regardless of what was selected. */}
      {assignedRole && RoleLimitsTable(assignedRole)}

      {activationUrl && expiresAt && (
        <div className="success-result-card-countdown">
          <ActivationCountdown infoSource={infoSource} expiresAt={expiresAt} onExpire={() => setExpired(true)} />
        </div>
      )}

      <ActivationLinkActions
        infoSource={infoSource}
        activationUrl={activationUrl}
        user={user}
        fullName={fullName}
        roleName={roleName}
        expirationDate={expiresAt}
        shareMessage={shareMessage}
        expired={expired}
        onSuccess={handleSuccess}
        onError={handleError}
      />

      {toast && (
        <p className={`success-result-card-toast success-result-card-toast--${toast.type}`} role="status">
          {toast.text}
        </p>
      )}

      <div className="success-result-card-footer">
        <button type="button" className="success-result-card-finish-btn" onClick={onFinish}>
          {__elakayText("finish", "Finish")}
        </button>
      </div>
    </div>
  );
};

export default SuccessResultCard;
